import { create } from 'zustand';

/**
 * Whether the browser has a network connection — and *only* that (021 FR-006b, research R-008).
 *
 * The prohibition is the point of this module. A failed HTTP response must never reach it: the
 * offline condition is connectivity, and a reachable-but-failing server is a different state that
 * the application already reports its own way, per screen.
 *
 * Conflating the two is the tempting "improvement" and it is forbidden three times over. It would
 * tell a practitioner with a working connection that they are offline, which is simply false. It
 * would hide a server fault behind a message about their network. And it would swallow the
 * `session.liveness_unavailable` 503 that 014 introduced *specifically* so an unanswerable
 * liveness check could not be read as a revoked session — this application has already paid for
 * that distinction once.
 *
 * The honest limitation, accepted deliberately: `navigator.onLine` reports whether the machine has
 * a route, not whether anything is reachable, so behind a captive portal it says online when
 * nothing is. That false NEGATIVE leaves behaviour exactly as it is today. The false POSITIVE the
 * "better" implementation would introduce is worse than today. The imprecision is accepted in one
 * direction only, and the spec's Assumptions say so.
 *
 * ---
 *
 * 034: THE NO-SETTER GUARANTEE IS STRUCTURAL, AND KEEPING IT STRUCTURAL TOOK CARE.
 *
 * The Angular class used a `#private` field so the writable signal was unreachable at runtime, and
 * `connectivity.spec.ts` asserted the whole public surface was `isOnline` — so a future caller
 * could not flip it from an error handler. A Zustand store exposes `setState` on the store object
 * itself, which would hand that ability back to anyone who imported it.
 *
 * So the store is MODULE-PRIVATE and only two readers are exported. `setState` is reachable from
 * nowhere outside this file, which is the same guarantee by a different mechanism rather than a
 * convention someone has to remember.
 */
const store = create<{ isOnline: boolean }>()(() => ({ isOnline: navigator.onLine }));

/** Read inside a component. Re-renders on change. */
export const useIsOnline = (): boolean => store((s) => s.isOnline);

/** Read outside React — the session store and the offline interceptor both need it. */
export const isOnline = (): boolean => store.getState().isOnline;

// No teardown: these outlive every consumer for the life of the document, so unsubscribing would
// be ceremony around listeners that can never be stale.
window.addEventListener('online', () => store.setState({ isOnline: true }));
window.addEventListener('offline', () => store.setState({ isOnline: false }));
